import { useEffect, useState, useMemo } from 'react';
import DefinitionParser from '../utils/definitionParser';
import useExperienceState from './useExperienceState';
import { toast } from 'sonner';

export default function useExperienceBuilder(activationId: number) {
    const [definition, setDefinition] = useState<Definition | null>(null);
    const [screenCount, setScreenCount] = useState<number>(0);
    const [processingFlowCount, setProcessingFlowCount] = useState<number>(0);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [hasIdErrors, setHasIdErrors] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [lastSavedDefinition, setLastSavedDefinition] =
        useState<Definition | null>(null);
    const {
        loadExperience,
        screens,
        allScreens,
        sharing,
        exportExperience,
        createSharingStation,
        deleteSharingStation,
        updateSharingStationName,
        addScreenToSharingStation,
        removeScreenFromSharingStation,
        deleteScreenFromSharingStation,
        updateSharingStationData,
    } = useExperienceState(activationId);

    const importDefinition = (definition: string, activationId: number) => {
        let parser = DefinitionParser.getInstance();

        try {
            parser.parseDefinition(definition);
        } catch (e) {
            toast.error('Invalid JSON. Check the format and try again.');
            return;
        }

        let parsedDefinition = parser.parseDefinition(definition);

        let {
            isValid,
            warnings,
            errors,
            screenCount,
            processingFlowCount,
            hasIdErrors,
        } = parser.analyzeDefinition(parsedDefinition);

        setIsValid(isValid);
        setWarnings(warnings);
        setErrors(errors);
        setScreenCount(screenCount);
        setProcessingFlowCount(processingFlowCount);
        setHasIdErrors(hasIdErrors);
        setDefinition(parsedDefinition);

        for (let error of errors) {
            console.error(error);
        }
        for (let warning of warnings) {
            console.warn(warning);
        }
        if (errors.length > 0) {
            toast.error('Invalid definition. Check the errors and try again.', {
                duration: 10000,
            });
        }

        loadExperience(parsedDefinition, activationId);
    };

    const updateDefinition = (definition: Definition) => {
        let parser = DefinitionParser.getInstance();
        let {
            isValid,
            warnings,
            errors,
            screenCount,
            processingFlowCount,
            hasIdErrors,
        } = parser.analyzeDefinition(definition);

        setIsValid(isValid);
        setWarnings(warnings);
        setErrors(errors);
        setProcessingFlowCount(processingFlowCount);
        setScreenCount(screenCount);
        setHasIdErrors(hasIdErrors);
        setDefinition(definition);
    };

    /*useEffect(() => {
        setScreenCount(screens.length);
    }, [screens]);*/

    // Memoize stringified values to avoid complex expressions in dependency array
    const screensString = useMemo(() => JSON.stringify(screens), [screens]);
    const sharingString = useMemo(() => JSON.stringify(sharing), [sharing]);

    useEffect(() => {
        if (definition) {
            let newDefinition = exportExperience(activationId);
            let oldDefinition = JSON.stringify(definition);
            let newDefinitionString = JSON.stringify(newDefinition);
            if (oldDefinition !== newDefinitionString) {
                console.log('Definition changed - updating state');
                console.log('Screens count:', screens.length);
                console.log(
                    'First screen elements:',
                    screens[0]?.elements?.length || 0,
                );
                updateDefinition(newDefinition);
            }
        }
    }, [
        activationId,
        screensString,
        sharingString,
        definition,
        exportExperience,
        screens,
    ]);

    const exportDefinition = () => {
        let parser = DefinitionParser.getInstance();
        return parser.stringifyDefinition(definition);
    };

    useEffect(() => {
        setHasUnsavedChanges(
            definition !== null &&
                JSON.stringify(definition) !==
                    JSON.stringify(lastSavedDefinition),
        );
    }, [definition, lastSavedDefinition]);

    return {
        definition,
        screenCount,
        processingFlowCount,
        warnings,
        errors,
        importDefinition,
        exportDefinition,
        isValid,
        screens, // Main experience screens only
        allScreens, // All screens (including sharing station screens)
        sharing,
        updateDefinition,
        hasUnsavedChanges,
        setLastSavedDefinition,
        hasIdErrors,
        createSharingStation,
        deleteSharingStation,
        updateSharingStationName,
        addScreenToSharingStation,
        removeScreenFromSharingStation,
        deleteScreenFromSharingStation,
        updateSharingStationData,
    };
}

// Removed unused diffString function
